"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom"
import {
  FiHome, FiTruck, FiCalendar, FiMessageSquare, FiLogOut, FiBell,
  FiMenu, FiX, FiChevronRight, FiChevronLeft, FiSearch, FiCreditCard,
  FiUser, FiSettings, FiStar, FiPlus
} from "react-icons/fi"
import { motion, AnimatePresence } from "framer-motion"
import { useAuth } from "../../contexts/AuthContext"
import { useNotifications } from "../../contexts/NotificationContext"
import NotificationCenter from "../client/NotificationCenter"
import { toast } from "react-toastify"
import { getSubscription, checkTrialStatus } from "../../services/subscriptionService"
import { getPaymentMethods } from '../../services/paymentService'
import { api } from '../../services/api';
import ContractSelectionModal from './ContractSelectionModal';
import SubscriptionModal from './SubscriptionModal';
import CommissionContractModal from './CommissionContractModal';

// --- Helpers de normalisation pour éviter les états incohérents ---
const normalizeBoolean = (value, defaultValue = false) => {
  if (value === true) return true;
  if (value === false) return false;
  return defaultValue;
};

const normalizeDateIso = (value, fallbackIso) => {
  if (!value) return fallbackIso || null;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallbackIso || null;
    return d.toISOString();
  } catch {
    return fallbackIso || null;
  }
};

const normalizeContractPayload = (payload) => {
  // Accepte différentes formes: {data: {...}} ou {...}
  if (!payload) return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return typeof payload === 'object' ? payload : null;
};

const normalizeTrialResponse = (resp, userTrialEndDateIso) => {
  const r = resp || {};
  const isTrialActive = normalizeBoolean(r.isTrialActive, true);
  const hasActiveContract = normalizeBoolean(r.hasActiveContract, false);
  const requiresAction = normalizeBoolean(r.requiresAction, false);
  const contractType = r.contractType || null;
  const code = r.code || (isTrialActive ? 'TRIAL_ACTIVE' : 'TRIAL_EXPIRED');
  const message = r.message || 'Statut de période d\'essai récupéré';
  const trialEndDate = normalizeDateIso(r.trialEndDate, userTrialEndDateIso);
  return {
    isTrialActive,
    hasActiveContract,
    contractType,
    requiresAction,
    code,
    message,
    trialEndDate
  };
};

// --- Éléments de Navigation ---
const navItems = [
  { icon: FiHome, label: "Tableau de bord", path: "/demenageur" },
  { icon: FiTruck, label: "Missions", path: "/demenageur/missions", badgeKey: "newMissions" },
  { icon: FiCalendar, label: "Disponibilités", path: "/demenageur/disponibilites" },
  { icon: FiMessageSquare, label: "Messages", path: "/demenageur/messages", badgeKey: "unreadMessages" },
  { icon: FiCreditCard, label: "Paiements", path: "/demenageur/paiements" },
];


// --- Sous-composants ---
const NavItem = ({ item, isActive, isSidebarOpen, badge, onClick }) => (
  <Link
    to={item.path}
    onClick={onClick}
    title={!isSidebarOpen ? item.label : ''}
    className={`relative flex items-center px-4 py-3 text-sm font-medium rounded-lg mx-2 transition-all duration-200 group ${
      isActive
        ? 'bg-amber-500/10 text-amber-400'
        : 'text-gray-400 hover:bg-slate-700/50 hover:text-white'
    }`}
  >
    <item.icon className={`w-5 h-5 transition-colors flex-shrink-0 ${isActive ? 'text-amber-400' : 'text-gray-500 group-hover:text-white'}`} />
    <AnimatePresence>
      {isSidebarOpen && (
        <motion.span 
          initial={{ opacity: 0, width: 0, x: -10 }}
          animate={{ opacity: 1, width: 'auto', x: 0, marginLeft: '12px' }}
          exit={{ opacity: 0, width: 0, x: -10, marginLeft: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="whitespace-nowrap"
        >
          {item.label}
        </motion.span>
      )}
    </AnimatePresence>
    {badge > 0 && (
      isSidebarOpen ? (
        <span className="ml-auto bg-amber-500 text-slate-900 text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">{badge}</span>
      ) : (
        <div className="absolute left-6 top-1.5 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-slate-800"></div>
      )
    )}
  </Link>
);


const Breadcrumbs = () => {
    const location = useLocation();
    const paths = location.pathname.split('/').filter(p => p && p !== 'demenageur');
    
    return (
        <nav className="flex items-center text-sm text-slate-400">
            <Link to="/demenageur" className="hover:text-amber-400">Tableau de bord</Link>
            {paths.map((path, index) => (
                <React.Fragment key={index}>
                    <FiChevronRight className="w-4 h-4 mx-1"/>
                    <Link to={`/demenageur/${paths.slice(0, index + 1).join('/')}`} className="capitalize hover:text-amber-400">
                        {path.replace('-', ' ')}
                    </Link>
                </React.Fragment>
            ))}
        </nav>
    );
};

// Composant BlockingOverlay
const BlockingOverlay = ({ localTrialEnd, localTrialStart, onContractSelected, onOpenSubscription, onOpenCommission }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasActiveCommission, setHasActiveCommission] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const now = new Date();
  const endDate = localTrialEnd ? new Date(localTrialEnd) : null;
  const isTrialEnded = endDate ? now > endDate : false;
  const isBeforeStart = localTrialStart ? now < new Date(localTrialStart) : false;

  // La gestion du défilement est supprimée pour éviter les problèmes

  useEffect(() => {
    const checkContracts = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Vérifier à la fois les abonnements et les contrats de commission
        const [subscriptionRes, commissionRes] = await Promise.all([
          api.getActiveSubscription(user.id).catch(e => {
            console.error('Erreur lors de la vérification de l\'abonnement:', e);
            return { data: null };
          }),
          api.getActiveCommission(user.id).catch(e => {
            console.error('Erreur lors de la vérification de la commission:', e);
            return { data: null };
          })
        ]);

        const subscription = subscriptionRes?.data?.data || subscriptionRes?.data || null;
        const commission = commissionRes?.data?.data || commissionRes?.data || null;

        console.log('[BlockingOverlay] Vérification des contrats:', {
          userId: user.id,
          hasSubscription: !!subscription,
          hasCommission: !!commission,
          subscription,
          commission
        });

        setHasActiveSubscription(!!subscription);
        setHasActiveCommission(!!commission);
      } catch (error) {
        console.error('Erreur lors de la vérification des contrats:', error);
      } finally {
        setLoading(false);
      }
    };

    checkContracts();
  }, [user?.id]);

  // Ne pas afficher le blocage s'il y a un contrat actif (abonnement ou commission)
  if (hasActiveSubscription || hasActiveCommission) {
    return null;
  }

  // Afficher un indicateur de chargement pendant la vérification
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl">
          <p>Vérification de votre abonnement...</p>
        </div>
      </div>
    );
  }

  const handleContractSelection = async (contractType) => {
    console.log('[BLOCK] Contrat sélectionné:', contractType);
    setIsSubmitting(true);
    const toastId = toast.loading('Traitement en cours...');
    
    try {
      if (contractType === 'subscription' && typeof onOpenSubscription === 'function') {
        // Toujours ouvrir la modale d'abonnement sans vérifier les méthodes de paiement
        toast.dismiss(toastId);
        onOpenSubscription();
        return;
      }
      
      if (contractType === 'commission' && typeof onOpenCommission === 'function') {
        toast.dismiss(toastId);
        onOpenCommission();
        return;
      }
      
      await onContractSelected(contractType);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('[BLOCK] Erreur lors de la sélection du contrat:', error);
      toast.update(toastId, {
        render: 'Une erreur est survenue. Veuillez réessayer.',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ne pas afficher le blocage si la période d'essai est en cours et non terminée
  if (!isTrialEnded && !isBeforeStart) {
    return null;
  }

  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      color: 'white'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '0.5rem',
        padding: '2rem',
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center'
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: '#fff'
        }}>
          {isBeforeStart ? 'Période d\'essai non commencée' : 'Période d\'essai terminée'}
        </h2>
        
        <p style={{
          marginBottom: '2rem',
          color: '#ccc',
          lineHeight: '1.6'
        }}>
          {isBeforeStart 
            ? `Votre période d'essai commencera le ${new Date(localTrialStart).toLocaleDateString()}.`
            : `Votre période d'essai est terminée. Veuillez choisir un type de contrat pour continuer.`}
        </p>

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: '2rem'
        }}>
          {/* Afficher le bouton Abonnement seulement si l'utilisateur n'a pas de contrat de commission actif */}
          {!hasActiveCommission && !loading && (
            <button
              onClick={() => handleContractSelection('subscription')}
              disabled={isSubmitting || hasActiveSubscription}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: isSubmitting || hasActiveSubscription ? '#6b7280' : '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: '600',
                cursor: isSubmitting || hasActiveSubscription ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || hasActiveSubscription ? 0.5 : 1,
                minWidth: '200px'
              }}
            >
              {isSubmitting ? 'Traitement...' : 'Abonnement Mensuel'}
            </button>
          )}
          
          {/* Afficher le bouton Commission seulement si l'utilisateur n'a pas d'abonnement actif */}
          {!hasActiveSubscription && !loading && (
            <button
              onClick={() => handleContractSelection('commission')}
              disabled={isSubmitting || hasActiveCommission}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: isSubmitting || hasActiveCommission ? '#6b7280' : '#f59e0b',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: '600',
                cursor: isSubmitting || hasActiveCommission ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || hasActiveCommission ? 0.5 : 1,
                minWidth: '200px'
              }}
            >
              {isSubmitting ? 'Traitement...' : 'Commission par Mission'}
            </button>
          )}
          
          {/* Message si aucun bouton n'est disponible */}
          {loading && (
            <p style={{ color: '#ccc', textAlign: 'center', width: '100%' }}>
              Vérification des contrats en cours...
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// --- Layout Principal ---
export default function DemenageurLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  // Initialisation avec des valeurs qui empêchent le clignotement
  const [trialStatus, setTrialStatus] = useState(() => {
    // Récupérer l'état précédent du localStorage s'il existe
    const savedState = typeof window !== 'undefined' ? localStorage.getItem('trialStatus') : null;
    
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // Vérifier si l'état sauvegardé est toujours valide
        if (parsed && typeof parsed === 'object') {
          return {
            isTrialActive: parsed.isTrialActive !== false, // Par défaut à true si non défini
            hasActiveContract: parsed.hasActiveContract === true,
            trialEndDate: parsed.trialEndDate || null,
            contractType: parsed.contractType || null,
            message: parsed.message || 'Vérification en cours...',
            requiresAction: parsed.requiresAction === true,
            code: parsed.code || 'CHECKING_STATUS',
            lastChecked: parsed.lastChecked || null
          };
        }
      } catch (e) {
        console.error('Erreur lors de la lecture de l\'état du trial:', e);
      }
    }
    
    // Valeurs par défaut si pas d'état sauvegardé
    return {
      isTrialActive: true,
      hasActiveContract: false,
      trialEndDate: null,
      contractType: null,
      message: 'Vérification en cours...',
      requiresAction: false,
      code: 'CHECKING_STATUS',
      lastChecked: null
    };
  });
  
  // Sauvegarder l'état dans le localStorage à chaque changement
  useEffect(() => {
    if (trialStatus && typeof window !== 'undefined') {
      localStorage.setItem('trialStatus', JSON.stringify(trialStatus));
    }
  }, [trialStatus]);
  const [unreadMessages, setUnreadMessages] = useState(3);
  const [newMissions, setNewMissions] = useState(1);
  const badgeCounts = { unreadMessages, newMissions };

  const { user, logout, setUser } = useAuth();
  // Déterminer tôt si l'utilisateur est déménageur pour éviter l'ordre des hooks
  const isDemenageur = (user?.is_demenageur === true) || (user?.role === 'demenageur');
  // Log de rendu pour vérifier le montage
  console.log('[DL] render', { userId: user?.id, role: user?.role, isDemenageur });
  // Bornes locales d'essai et évaluation locale, définies tôt pour les hooks suivants
  const localTrialStart = user?.trial_start_date || user?.trialStartDate || null;
  const localTrialEnd = user?.trial_end_date || user?.trialEndDate || null;
  const localEnded = React.useMemo(() => {
    try {
      const now = new Date();
      const start = localTrialStart ? new Date(localTrialStart) : null;
      const end = localTrialEnd ? new Date(localTrialEnd) : null;
      const beforeStart = start ? (now < start) : false;
      const afterEnd = end ? (now > end) : false;
      return Boolean(beforeStart || afterEnd);
    } catch (_) { return false; }
  }, [localTrialStart, localTrialEnd]);
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  
  const settingsItems = [
    { icon: FiUser, label: "Profil", path: "/demenageur/profil" },
    { icon: FiCreditCard, label: "Contrat", path: "/demenageur/contrat" },

    { icon: FiSettings, label: "Paramètres", path: "/demenageur/parametres" },
  ];
  
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  // Fonction utilitaire pour vérifier si un chemin est actif
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const pageTitle = navItems.find(item => isActive(item.path))?.label || 
                    settingsItems.find(item => isActive(item.path))?.label || 
                    "Bienvenue";

  // Effet pour vérifier la période d'essai et les contrats
  useEffect(() => {
    let isMounted = true;
    
    const checkTrialAndSubscription = async () => {
      if (!user?.id || !isDemenageur) {
        return;
      }

      try {
        
        // Normaliser les bornes d'essai: trial_start_date et trial_end_date (ISO)
        // Utiliser directement les dates de la base de données sans calcul
        const userTrialStartDate = (user.trial_start_date || user.trialStartDate) 
          ? new Date(user.trial_start_date || user.trialStartDate).toISOString() 
          : null;
        const userTrialEndDate = (user.trial_end_date || user.trialEndDate)
          ? new Date(user.trial_end_date || user.trialEndDate).toISOString()
          : null;
        
        // Vérifier d'abord si l'utilisateur a un contrat actif
        let hasActiveContract = false;
        let contractType = null;
        let requiresAction = false;
        let message = 'Vérification des contrats...';
        let code = 'CHECKING_CONTRACTS';
        let activeSubscription = null;
        let activeCommission = null;
        
        try {
          console.log('🔍 Vérification des contrats pour l\'utilisateur:', user.id);
          
          // Vérifier les contrats avec gestion d'erreur améliorée
          const [subscriptionRes, commissionRes] = await Promise.all([
            api.getActiveSubscription(user.id).catch(e => {
              console.error('❌ Erreur lors de la récupération de l\'abonnement:', e);
              return { data: null };
            }),
            api.getActiveCommission(user.id).catch(e => {
              console.error('❌ Erreur lors de la récupération de la commission:', e);
              return { data: null };
            })
          ]);
          
          // Extraire et normaliser les données des réponses
          activeSubscription = subscriptionRes?.data?.data || subscriptionRes?.data || null;
          activeCommission = commissionRes?.data?.data || commissionRes?.data || null;
          
          console.log('📊 Résultats de la vérification des contrats:', {
            hasSubscription: !!activeSubscription,
            hasCommission: !!activeCommission,
            subscription: activeSubscription,
            commission: activeCommission
          });
          
       
          
          // Vérifier si un contrat actif existe
          if (activeSubscription || activeCommission) {
            contractType = activeSubscription ? 'subscription' : 'commission';
            message = activeSubscription ? 'Abonnement actif' : 'Contrat de commission actif';
            code = 'CONTRACT_ACTIVE';
            
            
            if (isMounted) {
              setTrialStatus({
                isTrialActive: false,
                hasActiveContract: true,
                contractType,
                message,
                code,
                requiresAction: false,
                trialEndDate: userTrialEndDate,
                lastChecked: new Date().toISOString()
              });
              
              setShowContractModal(false);
              return; // Sortir de la fonction car on a trouvé un contrat actif
            }
          } else {
            
            // Vérifier si la période d'essai est expirée
            const now = new Date();
            const trialStart = userTrialStartDate ? new Date(userTrialStartDate) : null;
            const trialEndDate = new Date(userTrialEndDate);
            const isAfterEnd = now > trialEndDate;
            const isBeforeStart = trialStart ? now < trialStart : false;
            const isTrialEnded = isAfterEnd || isBeforeStart;
            
            if (isTrialEnded) {
              // Période d'essai expirée
              message = 'Votre période d\'essai est terminée. Veuillez choisir un type de contrat.';
              code = 'TRIAL_EXPIRED';
              requiresAction = true;
              
              
              if (isMounted) {
                setShowContractModal(true);
              }
            } else {
              // Période d'essai toujours active
              message = `Période d'essai active jusqu'au ${trialEndDate.toLocaleDateString()}`;
              code = 'TRIAL_ACTIVE';
              requiresAction = false;
              
            }
            
            if (isMounted) {
              setTrialStatus({
                isTrialActive: !isTrialEnded,
                hasActiveContract: false,
                contractType: null,
                message,
                code,
                requiresAction,
                trialEndDate: userTrialEndDate,
                trialStartDate: userTrialStartDate,
                lastChecked: new Date().toISOString()
              });
            }
          }
          

          
          try {
            const rawTrialResponse = await checkTrialStatus(user.id);
            const normalized = normalizeTrialResponse(rawTrialResponse, userTrialEndDate);

            if (isMounted) {
              setTrialStatus({
                ...normalized,
                trialStartDate: userTrialStartDate || normalized.trialStartDate,
                lastChecked: new Date().toISOString()
              });
              
              // Afficher la modale si nécessaire
              if (normalized.requiresAction) {
                setShowContractModal(true);
              } else {
                setShowContractModal(false);
              }
            }
            
            return; // Sortir de la fonction après la mise à jour
            
          } catch (trialError) {
            console.error('Erreur lors de la vérification de la période d\'essai:', trialError);
            
            // En cas d'erreur, on considère que la période d'essai est terminée
            const errorStatus = {
              isTrialActive: false,
              hasActiveContract: false,
              contractType: null,
              message: 'Impossible de vérifier le statut de la période d\'essai',
              code: 'TRIAL_EXPIRED',
              requiresAction: true,
              trialEndDate: userTrialEndDate,
              lastChecked: new Date().toISOString()
            };
            
            if (isMounted) {
              setTrialStatus(errorStatus);
              setShowContractModal(true);
            }
          }
          
        } catch (error) {
          console.error('❌ Erreur inattendue lors de la vérification des contrats actifs:', error);
          
          // En cas d'erreur, on met à jour l'état avec les informations d'erreur
          if (isMounted) {
            const errorStatus = {
              isTrialActive: false,
              hasActiveContract: false,
              contractType: null,
              message: 'Erreur lors de la vérification des contrats',
              code: 'ERROR',
              requiresAction: true,
              trialEndDate: userTrialEndDate,
              lastChecked: new Date().toISOString(),
              error: error.message
            };
            
            setTrialStatus(errorStatus);
            setShowContractModal(true);
          }
          return; // Sortir de la fonction en cas d'erreur
        }
        
        // Vérifier si un contrat a été détecté via la réponse de l'API
        const activeContract = activeSubscription || activeCommission;
        if (activeContract) {
          
          if (isMounted) {
            setTrialStatus({
              isTrialActive: false,
              hasActiveContract: true,
              contractType: activeContract.type || (activeSubscription ? 'subscription' : 'commission'),
              message: 'Contrat actif',
              code: 'CONTRACT_ACTIVE',
              requiresAction: false,
              trialEndDate: activeContract.end_date || userTrialEndDate,
              lastChecked: new Date().toISOString()
            });
            setShowContractModal(false);
          }
          return;
        }
        
        // Vérifier si la période d'essai est active
        const trialEndDate = userTrialEndDate;
        const now = new Date();
        const trialEndDateObj = new Date(trialEndDate);
        const isTrialEnded = now > trialEndDateObj;
        const isTrialActive = !isTrialEnded; // Simplification de la logique de période d'essai
    
        
        // Mettre à jour l'état en fonction du statut de la période d'essai
        if (isTrialActive) {
          const daysRemaining = Math.ceil((trialEndDateObj - now) / (1000 * 60 * 60 * 24));
          const message = `Période d'essai active - ${daysRemaining} jour(s) restant(s)`;
          
          if (isMounted) {
            setTrialStatus({
              isTrialActive: true,
              hasActiveContract: false,
              contractType: null,
              message,
              code: 'TRIAL_ACTIVE',
              requiresAction: false,
              trialEndDate,
              lastChecked: new Date().toISOString()
            });
            setShowContractModal(false);
          }
        } else {
          // Période d'essai expirée
          if (isMounted) {
            const expiredStatus = {
              isTrialActive: false,
              hasActiveContract: false,
              contractType: null,
              message: 'Votre période d\'essai est terminée. Veuillez choisir un type de contrat.',
              code: 'TRIAL_EXPIRED',
              requiresAction: true,
              trialEndDate,
              lastChecked: new Date().toISOString()
            };
            
            setTrialStatus(expiredStatus);
            setShowContractModal(true);
            
            // Empêcher la navigation
            window.history.pushState(null, '', window.location.pathname);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de la période d\'essai:', error);
        
        if (isMounted) {
          // En cas d'erreur 403 (période d'essai terminée) ou autre erreur, forcer l'affichage de la modale
          if (error.response?.status === 403 || error.code === 'TRIAL_EXPIRED' || error.response?.status === 404) {
            setTrialStatus({
              isTrialActive: false,
              hasActiveContract: false,
              code: 'TRIAL_EXPIRED',
              message: 'Veuillez sélectionner un type de contrat pour continuer',
              requiresAction: true
            });
            setShowContractModal(true);
            window.history.pushState(null, '', window.location.pathname);
          }
        }
      }
    };

    // Vérifier périodiquement (toutes les 5 minutes)
    const intervalId = setInterval(checkTrialAndSubscription, 5 * 60 * 1000);
    
    // Vérifier immédiatement
    console.log('[TrialCheck] Trigger immediate check', { userId: user?.id, isDemenageur });
    checkTrialAndSubscription();

    // Nettoyer l'intervalle lors du démontage
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [user?.id, isDemenageur, location.pathname]);

  const handleContractSelected = async (contractType, extra = {}) => {
    console.log('[CONTRACT] Sélection du type de contrat:', contractType, 'Données supplémentaires:', extra);
    const toastId = toast.loading('Traitement de votre demande...');
    let contractId = null;

    try {
      let subscriptionPlan = null;
      let contractResponse = null;
      
      // Vérifier que le plan d'abonnement est valide
      if (contractType === 'subscription') {
        const planId = extra.plan || 'basic';
        try {
          console.log('[CONTRACT] Récupération des plans d\'abonnement...');
          const response = await api.get('/contracts/subscription-plans');
          // The response might be in response.data (direct array) or response.data.data (nested data property)
          const plans = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          console.log('[CONTRACT] Plans disponibles:', plans);
          
          if (!plans || plans.length === 0) {
            console.error('[CONTRACT] Aucun plan d\'abonnement trouvé dans la réponse:', response);
            throw new Error('Aucun plan d\'abonnement disponible pour le moment.');
          }
          
          // Essayer de trouver le plan par plan_id ou name (insensible à la casse)
          subscriptionPlan = plans.find(p => {
            if (!p) return false;
            const planIdMatch = p.plan_id && p.plan_id.toLowerCase() === planId.toLowerCase();
            const nameMatch = p.name && p.name.toLowerCase() === planId.toLowerCase();
            return planIdMatch || nameMatch;
          });
          
          if (!subscriptionPlan) {
            console.error('[CONTRACT] Plan non trouvé. Plans disponibles:', plans.map(p => p ? { 
              id: p.id, 
              plan_id: p.plan_id, 
              name: p.name 
            } : 'invalid-plan'));
            throw new Error(`Le plan d'abonnement "${planId}" n'existe pas.`);
          }
          
          console.log('[CONTRACT] Plan d\'abonnement trouvé:', subscriptionPlan);
        } catch (error) {
          console.error('Erreur lors de la vérification du plan:', error);
          throw new Error('Impossible de vérifier le plan d\'abonnement: ' + (error.response?.data?.message || error.message));
        }
      }

      // Préparer les données du contrat selon le schéma attendu par l'API
      const contractData = {
        type: contractType,
        status: 'pending',
        startDate: new Date().toISOString(),
        terms: contractType === 'subscription' 
          ? `Abonnement ${subscriptionPlan?.name || extra.plan || 'basic'}` 
          : 'Contrat de commission par mission',
        ...(contractType === 'subscription' && subscriptionPlan && {
          // Backend expects `subscriptionPlanId` (can be id or plan_id string)
          subscriptionPlanId: String(subscriptionPlan.id || subscriptionPlan.plan_id),
          // Optional references for traceability
          plan_id: subscriptionPlan.plan_id,
          billingCycle: extra.billingCycle || 'monthly',
          autoRenew: true,
          subscriptionType: 'recurring'
        }),
        ...(contractType === 'commission' && {
          commissionRate: 0.15, // 15% de commission
          fixedAmount: 0,
          // Champs pour la validation côté client
          termsAccepted: true,
          termsAcceptedAt: new Date().toISOString(),
          signatureDataUrl: extra.signatureDataUrl || null
        })
      };

      console.log('[CONTRACT] Création du contrat avec les données:', contractData);

      // Créer le contrat
      console.log('[CONTRACT] Envoi de la requête de création de contrat...');
      const response = await api.post('/contracts', contractData);
      console.log('[CONTRACT] Réponse brute du serveur:', response);
      
      // Vérifier si la réponse est vide
      if (!response) {
        console.error('[CONTRACT] Aucune réponse du serveur');
        throw new Error('Le serveur n\'a pas répondu');
      }
      
      // Vérifier si la réponse contient des données
      contractResponse = response.data || response;
      console.log('[CONTRACT] Données du contrat créé:', contractResponse);
      
      // Vérifier si la réponse est vide ou invalide
      if (!contractResponse || (typeof contractResponse === 'object' && Object.keys(contractResponse).length === 0)) {
        console.error('[CONTRACT] Réponse vide ou invalide du serveur');
        throw new Error('Réponse de création de contrat invalide: données manquantes');
      }
      
      // Extraire l'ID du contrat de différentes manières possibles
      contractId = contractResponse.id || 
                  (contractResponse.data && contractResponse.data.id) || 
                  (contractResponse.contract && contractResponse.contract.id) ||
                  (contractResponse.contractId);
                  
      if (!contractId) {
        console.error('[CONTRACT] Impossible de trouver l\'ID du contrat dans la réponse:', contractResponse);
        throw new Error('Impossible de récupérer l\'ID du contrat depuis la réponse du serveur');
      }
      
      console.log('[CONTRACT] Contrat créé avec succès, ID:', contractId);
      
      // Si c'est un abonnement, procéder à l'activation
      if (contractType === 'subscription') {
        try {
          const methods = await getPaymentMethods(user?.id).catch((err) => {
            console.error('[CONTRACT] Erreur lors de la récupération des méthodes de paiement:', err);
            return [];
          });
          
          if (methods.length > 0) {
            try {
              // Méthode de paiement disponible, procéder à l'activation
              toast.update(toastId, {
                render: 'Activation de votre abonnement...',
                type: 'info',
                isLoading: true
              });

              const planId = extra.plan || 'basic';
              const paymentMethodId = methods[0].id;
              
              console.log('[CONTRACT] Tentative d\'activation de l\'abonnement avec:', {
                userId: user?.id,
                plan: planId,
                paymentMethodId,
                contractId: contractId
              });

              try {
                // Essayer d'activer l'abonnement
                await api.post('/subscriptions/start', {
                  userId: user?.id,
                  plan: planId,
                  paymentMethodId,
                  contractId: contractId
                });
                console.log('[CONTRACT] Abonnement activé avec succès');
              } catch (activationError) {
                console.warn('[CONTRACT] Échec de l\'activation de l\'abonnement, mais le contrat est valide. Erreur:', activationError);
                // On continue quand même car le contrat est valide
                toast.update(toastId, {
                  render: 'Le contrat a été créé mais une erreur est survenue lors de l\'activation. Veuillez réessayer plus tard.',
                  type: 'warning',
                  isLoading: false,
                  autoClose: 5000
                });
              }
            } catch (error) {
              console.error('[CONTRACT] Erreur lors de la préparation de l\'activation:', error);
              // On continue quand même car le contrat est valide
            }
          } else {
            // Pas de méthode de paiement, rediriger vers la page de paiement
            navigate(`/demenageur/paiements?subscription_plan=${extra.plan || 'basic'}&contract_id=${contractId}`);
            return;
          }
        } catch (error) {
          console.error('[CONTRACT] Erreur lors de l\'activation de l\'abonnement:', error);
          // On ne lance pas d'erreur pour ne pas bloquer le processus
          toast.update(toastId, {
            render: 'Le contrat a été créé mais une erreur est survenue lors de l\'activation. Veuillez réessayer plus tard.',
            type: 'warning',
            isLoading: false,
            autoClose: 5000
          });
        }
      }
      
      // Mise à jour de l'état de l'utilisateur et du contrat
      const updatedTrialStatus = {
        hasActiveContract: true,
        code: 'CONTRACT_ACTIVE',
        message: contractType === 'subscription' ? 'Abonnement actif' : 'Contrat de commission actif',
        contractType: contractType,
        isTrialActive: false,
        requiresAction: false,
        trialEndDate: user?.trial_end_date || localTrialEnd,
        lastChecked: new Date().toISOString(),
        contractId: contractId
      };
      
      // Mettre à jour l'état de l'utilisateur
      if (user) {
        setUser(prev => ({
          ...prev,
          has_active_contract: true,
          contract_type: contractType,
          contract_id: contractId
        }));
      }
      
      // Mettre à jour l'état du trial
      setTrialStatus(updatedTrialStatus);
      
      // Sauvegarder dans le localStorage
      localStorage.setItem('trialStatus', JSON.stringify(updatedTrialStatus));
      
      // Fermer toutes les modales
      setShowContractModal(false);
      setShowSubscriptionModal(false);
      setShowCommissionModal(false);
      
      // Afficher le message de succès
      toast.update(toastId, {
        render: contractType === 'subscription' 
          ? 'Votre abonnement a été activé avec succès !' 
          : 'Votre contrat a été créé avec succès !',
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      
    } catch (error) {
      console.error('[CONTRACT] Erreur lors de la création du contrat:', error);
      toast.update(toastId, {
        render: `Erreur lors de la création du contrat: ${error.message}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
      
      // Fermer les modales en cas d'erreur
      setShowContractModal(false);
      setShowSubscriptionModal(false);
      setShowCommissionModal(false);
    }
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false);
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) setShowNotifications(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const firstName = user?.firstName || user?.first_name || "";
  const lastName = user?.lastName || user?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const userInitial = (firstName?.[0] || "D").toUpperCase();
  const userEmail = user?.email || "demenageur@example.com";

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/connexion');
      toast.success("Vous avez été déconnecté.");
    } catch (error) {
      toast.error('Erreur lors de la déconnexion.');
    }
  };
  
  // Utilisation de useMemo pour éviter les recalculs inutiles
  const isInitialTrialCheckPending = React.useMemo(() => {
    return isDemenageur && !trialStatus?.lastChecked;
  }, [isDemenageur, trialStatus?.lastChecked]);

  // (variables localTrialStart/localTrialEnd/localEnded définies plus haut)


  // Logs centralisés pour observer l'état de blocage et les bornes d'essai
  useEffect(() => {
    console.log('[TRIAL_DEBUG]', {
      now: new Date().toISOString(),
      localTrialStart,
      localTrialEnd,
      trialStatus,
      isDemenageur,
      userRole: user?.role,
      hasActiveContract: trialStatus?.hasActiveContract,
      isTrialActive: trialStatus?.isTrialActive,
      localEnded
    });
  }, [localTrialStart, localTrialEnd, trialStatus, isDemenageur, user?.role, localEnded]);
  
  // Vérification finale pour décider d'afficher l'écran de blocage
  const shouldBlock = React.useMemo(() => {
    if (!isDemenageur) {
      console.log('[BLOCK] Pas un déménageur, pas de blocage');
      return false;
    }

    // Vérifier si l'utilisateur a un contrat actif
    if (trialStatus?.hasActiveContract) {
      console.log('[BLOCK] Contrat actif détecté, pas de blocage');
      return false;
    }

    const now = new Date();
    const endDate = localTrialEnd ? new Date(localTrialEnd) : null;
    
    // Vérifier uniquement si nous sommes après la date de fin
    if (endDate && now > endDate) {
      console.log('[BLOCK] Période d\'essai terminée (basé sur la date)');
      // Forcer la mise à jour du statut si nécessaire
      if (trialStatus?.code !== 'TRIAL_EXPIRED') {
        setTrialStatus(prev => ({
          ...prev,
          isTrialActive: false,
          hasActiveContract: false,
          message: 'Période d\'essai terminée',
          code: 'TRIAL_EXPIRED',
          requiresAction: true,
          lastChecked: new Date().toISOString()
        }));
      }
      return true;
    }
    
    console.log('[BLOCK] La période d\'essai est toujours active');
    return false;
  }, [isDemenageur, localTrialEnd, trialStatus?.hasActiveContract, trialStatus?.code]);
  
  // S'assurer que le défilement est toujours actif
  useEffect(() => {
      document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Forcer la mise à jour de l'état si un contrat a été sélectionné mais que l'état n'est pas cohérent
  useEffect(() => {
    if (trialStatus?.contractType && !trialStatus.hasActiveContract) {
      setTrialStatus(prev => ({
        ...prev,
        hasActiveContract: true,
        code: 'CONTRACT_SELECTED',
        isTrialActive: false
      }));
    }
  }, [trialStatus?.contractType, trialStatus?.hasActiveContract]);


  // Rendu de blocage via portail supprimé pour éviter duplications; BlockingOverlayInLayout sera utilisé

  // Si on est en train de vérifier le statut, on affiche un écran de chargement
  if (trialStatus?.code === 'CHECKING_STATUS') {
    console.log('[BLOCK] Affichage de l\'écran de chargement');
    return (
      <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
          <p className="text-slate-300">Vérification de votre accès...</p>
        </div>
      </div>
    );
  }

  // Écran de pré-chargement tant que le statut d'essai n'a pas été vérifié (évite le besoin d'actualiser)
  if (isInitialTrialCheckPending) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"></div>
          <p className="text-slate-300">Initialisation de votre espace déménageur…</p>
        </div>
      </div>
    );
  }

  // This block is no longer needed as we're using the BlockingOverlay component
  // for all blocking scenarios
  if (false) {
    return (
      <div className="fixed inset-0 z-[100]">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-2xl w-full p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Période d'essai terminée</h1>
            <p className="text-slate-300 mb-6">Votre période d'essai est terminée. Veuillez choisir un type de contrat pour continuer.</p>
            <div className="bg-slate-700/50 border border-slate-600 rounded-lg mb-6 p-4">
              <p className="text-slate-300 mb-2">Fin de période d'essai :</p>
              <p className="text-lg font-semibold text-white">{new Date(trialStatus?.trialEndDate).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <ContractSelectionModal visible={true} onClose={() => {}} onContractSelected={handleContractSelected} />
          </div>
        </div>
      </div>
    );
  }

  

  return (
    <div className="min-h-screen bg-slate-900 flex text-slate-200">
      <BlockingOverlayInLayout 
        trialStatus={trialStatus}
        localTrialStart={localTrialStart}
        localTrialEnd={localTrialEnd}
        onContractSelected={handleContractSelected}
        onOpenSubscription={() => setShowSubscriptionModal(true)}
        onOpenCommission={() => setShowCommissionModal(true)}
      />
      
      <motion.aside
        initial={false}
        animate={{ width: isSidebarOpen ? "280px" : "88px" }}
        className="hidden md:flex flex-col bg-slate-800/50 border-r border-slate-700/50"
      >
        <div className="flex items-center justify-between h-20 px-4 border-b border-slate-700/50">
          <AnimatePresence>
          {isSidebarOpen && (
            <motion.div 
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                className="flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900">L</span>
              </div>
              <h1 className="text-xl font-bold text-white">Liberty Pro</h1>
            </motion.div>
          )}
          </AnimatePresence>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 rounded-full text-slate-400 hover:bg-slate-700/50">
            {isSidebarOpen ? <FiChevronLeft size={20} /> : <FiChevronRight size={20} />}
          </button>
        </div>
        
        <nav className="flex-1 flex flex-col justify-between overflow-y-auto pt-6">
          <div>
            <h3 className={`px-6 text-xs font-semibold text-slate-500 uppercase mb-2 ${isSidebarOpen ? '' : 'text-center'}`}>{isSidebarOpen ? 'Menu' : '•••'}</h3>
            <div className="space-y-2">
              {navItems.map((item) => <NavItem key={item.path} item={item} isActive={isActive(item.path)} isSidebarOpen={isSidebarOpen} badge={badgeCounts[item.badgeKey]}/>)}
            </div>
            <h3 className={`px-6 text-xs font-semibold text-slate-500 uppercase mt-8 mb-2 ${isSidebarOpen ? '' : 'text-center'}`}>{isSidebarOpen ? 'Compte' : '•••'}</h3>
            <div className="space-y-2">
              {settingsItems.map((item) => <NavItem key={item.path} item={item} isActive={isActive(item.path)} isSidebarOpen={isSidebarOpen}/>)}
            </div>
          </div>
          
          <div className="p-4">
            <button onClick={handleLogout} className="w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors group">
              <FiLogOut className="w-5 h-5"/>
              <AnimatePresence>{isSidebarOpen && <motion.span initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="ml-3">Déconnexion</motion.span>}</AnimatePresence>
            </button>
          </div>
        </nav>
      </motion.aside>

      <div className="flex-1 flex flex-col max-w-full overflow-visible">
        <header className="relative z-30 flex items-center justify-between h-20 px-4 sm:px-8 bg-slate-900/50 backdrop-blur-sm border-b border-slate-700/50 flex-shrink-0">
            <div className="flex items-center gap-2">
                <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-slate-400 md:hidden"><FiMenu size={24} /></button>
            </div>
            <div className="flex items-center gap-4">
                <div ref={notificationsRef} className="relative">
                    <button onClick={() => setShowNotifications(s => !s)} className="p-2 text-slate-400 hover:text-white rounded-full hover:bg-slate-700/50">
                        <FiBell size={20}/>
                        {unreadCount > 0 && <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-slate-900"></span>}
                    </button>
                    <AnimatePresence>
                        {showNotifications && (
                            <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-10}} className="absolute top-12 right-0 w-80 z-20">
                                <NotificationCenter onClose={() => setShowNotifications(false)} variant="dark" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div ref={userMenuRef} className="relative">
                    <button onClick={() => setShowUserMenu(s => !s)} className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 border-2 border-slate-600 flex items-center justify-center font-bold">{userInitial}</div>
                    </button>
                    <AnimatePresence>
                        {showUserMenu && (
                             <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-10}} className="absolute top-14 right-0 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-20">
                                <div className="p-4 border-b border-slate-700">
                                    <p className="font-semibold text-white">{fullName}</p>
                                    <p className="text-xs text-slate-400 truncate">{userEmail}</p>
                                    <div className="mt-2">
                                        <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 rounded-full">
                                            {user?.subscription_type || 'Basique'}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-2">
                                    <Link to="/demenageur/profil" onClick={() => setShowUserMenu(false)} className="block w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-700/50">Profil</Link>
                                    <Link to="/demenageur/parametres" onClick={() => setShowUserMenu(false)} className="block w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-700/50">Paramètres</Link>
                                </div>
                                <div className="p-2 border-t border-slate-700">
                                    <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md">Déconnexion</button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
            <div className="mb-6">
                <Breadcrumbs />
                <h1 className="text-3xl font-bold text-white mt-1">{pageTitle}</h1>
            </div>
            <Outlet />
        </main>
      
    </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsMobileMenuOpen(false)} className="fixed inset-0 bg-black/60 z-40 md:hidden" />
            <motion.div initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} transition={{ type: 'tween', ease: 'easeInOut' }} className="fixed top-0 left-0 h-full w-72 bg-slate-800 z-50 flex flex-col">
              <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <h2 className="font-bold text-white">Liberty Pro</h2>
                <button onClick={() => setIsMobileMenuOpen(false)}><FiX/></button>
              </div>
              <nav className="p-2 space-y-2 flex-1 mt-4">
                {navItems.map(item => <NavItem key={item.path} item={item} isActive={isActive(item.path)} isSidebarOpen={true} onClick={() => setIsMobileMenuOpen(false)} badge={badgeCounts[item.badgeKey]}/>)}
                <div className="pt-4 border-t border-slate-700 mt-4">
                  {settingsItems.map(item => <NavItem key={item.path} item={item} isActive={isActive(item.path)} isSidebarOpen={true} onClick={() => setIsMobileMenuOpen(false)} />)}
                </div>
              </nav>
              <div className="p-4 border-t border-slate-700">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 text-red-400 hover:bg-red-500/10 rounded-lg">
                  <FiLogOut/> Déconnexion
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {showSubscriptionModal && (
        <SubscriptionModal
          onClose={() => setShowSubscriptionModal(false)}
          onConfirm={async (plan) => {
            await handleContractSelected('subscription', { plan });
            setShowSubscriptionModal(false);
          }}
        />
      )}
      {showCommissionModal && (
        <CommissionContractModal
          onClose={() => setShowCommissionModal(false)}
          onConfirm={async (signatureDataUrl) => {
            await handleContractSelected('commission', { signatureDataUrl });
            setShowCommissionModal(false);
          }}
        />
      )}
    </div>
  );
};



// Add BlockingOverlay to the layout
const BlockingOverlayInLayout = ({ trialStatus, localTrialStart, localTrialEnd, onContractSelected, onOpenSubscription, onOpenCommission }) => {
  const location = useLocation();
  
  // Ne pas afficher le blocage sur la page de paiement
  if (location.pathname.startsWith('/demenageur/paiements')) {
    return null;
  }
  
  // Ne pas afficher le blocage s'il y a un contrat actif ou si aucune action n'est requise
  if (trialStatus?.hasActiveContract || !trialStatus?.requiresAction) {
    return null;
  }
  
  // Ajout d'un log pour le débogage
  console.log('[BlockingOverlayInLayout] Affichage du blocage', { 
    hasActiveContract: trialStatus?.hasActiveContract, 
    requiresAction: trialStatus?.requiresAction,
    trialStatus 
  });
  
  return (
    <BlockingOverlay
      localTrialStart={localTrialStart}
      localTrialEnd={localTrialEnd}
      onContractSelected={onContractSelected}
      onOpenSubscription={onOpenSubscription}
      onOpenCommission={onOpenCommission}
    />
  );
};